/**
 * Presentation layer over a customer-safe quantity ladder
 * (`CustomerQuantityLadderRowSchema[]`) — Brad's portal feedback,
 * 2026-10-02: "you should see the discounts at a glance for buying a
 * larger quantity".
 *
 * The backend's ladder rows already carry a `total_pence`/`unit_price_pence`
 * per quantity; what this module adds is the **display** arithmetic a page
 * renders on top of them — the saving badge and the "best value" marker —
 * computed once, here, from the same unit prices the page already shows,
 * so both ordering pages (configure, reorder) render identical numbers
 * from one formula instead of two inline copies that could drift.
 *
 * Savings are never invented: `savingPct` is a plain percentage difference
 * between this row's own unit price and the smallest-quantity row's unit
 * price, both read straight off the rows the backend priced. A row with no
 * price (`available: false`) never gets a saving or a best-value badge.
 */

export interface QuantityLadderDisplayRow {
  quantity: number;
  available: boolean;
  totalPence: number | null;
  unitPricePence: number | null;
  isSelected: boolean;
}

export interface QuantityLadderPresentationRow extends QuantityLadderDisplayRow {
  /** Per-unit saving against the smallest-quantity priced row, as a
   *  rounded percentage. `null` when this row has no price, or when it
   *  IS the baseline row (0%, not null — a real, honest "no saving yet"). */
  savingPct: number | null;
  /** True on exactly one row: the smallest quantity among the priced rows
   *  tied at the ladder's lowest unit price (more of the same unit price
   *  is not better value). */
  isBestValue: boolean;
}

/**
 * Attach `savingPct`/`isBestValue` to each row, baselined against the
 * smallest-quantity row that actually has a price — mirrors the backend's
 * own `_build_quantity_ladder` baseline choice (savings are a volume
 * story, quoted against the least amount a customer could buy), computed
 * independently here so the page never has to trust a field it cannot
 * itself verify against the prices on screen.
 */
export function presentQuantityLadder(
  rows: readonly QuantityLadderDisplayRow[],
): QuantityLadderPresentationRow[] {
  const priced = rows
    .filter((row) => row.available && row.unitPricePence != null)
    .sort((a, b) => a.quantity - b.quantity);

  const baselineUnitPrice = priced[0]?.unitPricePence ?? null;
  const bestUnitPrice =
    priced.length > 0
      ? Math.min(...priced.map((row) => row.unitPricePence as number))
      : null;
  // `priced` is sorted by quantity, so the first match is the smallest.
  const bestValueQuantity =
    priced.find((row) => row.unitPricePence === bestUnitPrice)?.quantity ??
    null;

  return rows.map((row) => {
    if (
      !row.available ||
      row.unitPricePence == null ||
      baselineUnitPrice == null
    ) {
      return { ...row, savingPct: null, isBestValue: false };
    }
    const savingPct =
      baselineUnitPrice > 0
        ? Math.round((1 - row.unitPricePence / baselineUnitPrice) * 100)
        : 0;
    return {
      ...row,
      savingPct,
      isBestValue: row.quantity === bestValueQuantity,
    };
  });
}

/** Below this a per-unit saving is rounding noise ("Save 1%"), not a reason
 *  to buy more, so it gets no badge. */
export const MIN_SAVING_PCT_TO_SHOW = 5;

/** "Save 18%" for a saving worth showing, otherwise nothing. */
export function savingLabel(savingPct: number | null): string | null {
  if (savingPct == null || savingPct < MIN_SAVING_PCT_TO_SHOW) return null;
  return `Save ${savingPct}%`;
}
